import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { OrdersService } from '../../../api/services/ordersService';
import { OrderErrorsService } from '../../../api/services/orderErrorsService';
import { IOrderDetail, IOrderSummary } from '../../../types/api/response/ordersresponse';
import { IOrderError } from '../../../types/api/response/ordererrorsresponse';
import { OrderErrorsPanel } from './OrderErrorsPanel';
import { DispatchOrderDialog } from '../DispatchOrderDialog';

interface Props {
  selectedOrder: IOrderSummary;
  isOpenDialog: boolean;
  setIsOpenDialog: (open: boolean) => void;
}

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" mt={0.25}>
      {value || '—'}
    </Typography>
  </Box>
);

const statusColor = (
  status: string,
): 'default' | 'warning' | 'info' | 'success' | 'error' | 'primary' => {
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      return 'success';
    case 'CANCELLED':
      return 'error';
    case 'ONWAY':
      return 'info';
    case 'ASSIGNED':
      return 'primary';
    case 'APPROVED':
      return 'warning';
    default:
      return 'default';
  }
};

const formatDate = (val?: string | null) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

const formatCurrency = (val?: number | null) => {
  if (val == null) return '—';
  return `$${val.toFixed(2)}`;
};

const formatMetadataKey = (key: string) =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const ViewOrderDialog = ({ selectedOrder, isOpenDialog, setIsOpenDialog }: Props) => {
  const [detail, setDetail] = useState<IOrderDetail | null>(null);
  const [errors, setErrors] = useState<IOrderError[] | null>(null);
  const [canViewErrors, setCanViewErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [isDispatchOpen, setIsDispatchOpen] = useState(false);

  const refreshDetail = () => {
    OrdersService.getOrderDetail(selectedOrder.order_number)
      .then(setDetail)
      .catch(() => {});
  };

  useEffect(() => {
    if (!isOpenDialog) return;
    setTab(0);
    setDetail(null);
    setErrors(null);
    setCanViewErrors(false);
    setLoading(true);

    const detailPromise = OrdersService.getOrderDetail(selectedOrder.order_number)
      .then(setDetail)
      .catch(() => {});

    const errorsPromise = OrderErrorsService.getOrderErrors(selectedOrder.order_number)
      .then((res) => {
        setCanViewErrors(true);
        setErrors(res);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          // No "Order Diagnostics" authority — the tab stays absent, not disabled.
          setCanViewErrors(false);
        } else {
          // Authorized, but the request itself failed — show an error state rather than
          // silently reporting "no errors".
          setCanViewErrors(true);
          setErrors(null);
        }
      });

    Promise.all([detailPromise, errorsPromise]).finally(() => setLoading(false));
  }, [isOpenDialog, selectedOrder.order_number]);

  return (
    <Dialog
      open={isOpenDialog}
      onClose={() => setIsOpenDialog(false)}
      slotProps={{
        paper: {
          sx: { width: { xs: '95%', sm: 640 }, maxHeight: '85vh' },
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography fontWeight={600}>
          Order — {selectedOrder.ds_order_number ?? selectedOrder.order_number}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={loading || !detail}
            onClick={() => setIsDispatchOpen(true)}
          >
            Rerun
          </Button>
          <IconButton
            size="small"
            onClick={() => setIsOpenDialog(false)}
            sx={{ color: 'grey.700' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {loading || !detail ? (
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Details" />
            <Tab label="Status History" />
            {canViewErrors && (
              <Tab label={`Errors${errors && errors.length > 0 ? ` (${errors.length})` : ''}`} />
            )}
          </Tabs>
          <DialogContent sx={{ pt: 2 }}>
            {tab === 0 && <DetailsTab detail={detail} />}
            {tab === 1 && <StatusHistoryTab detail={detail} />}
            {tab === 2 && canViewErrors && <OrderErrorsPanel errors={errors} />}
          </DialogContent>
        </>
      )}

      <DispatchOrderDialog
        selectedOrder={selectedOrder}
        isOpen={isDispatchOpen}
        setIsOpen={setIsDispatchOpen}
        onDispatch={refreshDetail}
      />
    </Dialog>
  );
};

// ─── Details Tab ─────────────────────────────────────────────────────────────

const DetailsTab = ({ detail }: { detail: IOrderDetail }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%' }}>
    {/* Status chips */}
    <Box display="flex" gap={1} flexWrap="wrap">
      <Chip label={detail.order_status} color={statusColor(detail.order_status)} size="small" />
      <Chip label={detail.order_type?.toUpperCase()} variant="outlined" size="small" />
      <Chip label={`Payment: ${detail.payment_status}`} variant="outlined" size="small" />
      {detail.is_18_plus && <Chip label="18+" color="error" size="small" />}
      {detail.has_frozen_goods && <Chip label="Frozen" color="info" size="small" />}
    </Box>

    {detail.order_details_link && (
      <Link
        href={detail.order_details_link}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
      >
        View order details in Locus →
      </Link>
    )}

    <Divider />

    {/* Customer & delivery */}
    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
      <Field label="Customer" value={detail.customer_name} />
      <Field label="Phone" value={detail.customer_phone} />
      <Field label="Address" value={detail.order_address} />
      <Field label="House No" value={detail.order_houseno} />
      <Field label="Recipient Name" value={detail.recipient_name} />
      <Field label="Recipient Phone" value={detail.recipient_phone} />
      <Field label="Order Date" value={formatDate(detail.order_date)} />
      {detail.slot_start && (
        <Field label="Slot" value={`${detail.slot_start} – ${detail.slot_end}`} />
      )}
      {detail.comment && <Field label="Comment" value={detail.comment} />}
      {detail.coupon_code && <Field label="Coupon" value={detail.coupon_code} />}
    </Box>

    <Divider />

    {/* Items */}
    <Box>
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Items ({detail.items?.length ?? 0})
      </Typography>
      <Table
        size="small"
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell
              sx={{
                py: 0.75,
                fontWeight: 700,
                fontSize: 11,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Product
            </TableCell>
            <TableCell
              sx={{
                py: 0.75,
                fontWeight: 700,
                fontSize: 11,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              SKU
            </TableCell>
            <TableCell
              align="center"
              sx={{
                py: 0.75,
                fontWeight: 700,
                fontSize: 11,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Qty
            </TableCell>
            <TableCell
              align="right"
              sx={{
                py: 0.75,
                fontWeight: 700,
                fontSize: 11,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Unit
            </TableCell>
            <TableCell
              align="right"
              sx={{
                py: 0.75,
                fontWeight: 700,
                fontSize: 11,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.items?.map((item, idx) => (
            <TableRow
              key={idx}
              sx={{
                '&:last-child td': { borderBottom: 0 },
                '&:hover': { backgroundColor: '#fafafa' },
              }}
            >
              <TableCell sx={{ py: 0.75, fontSize: 12 }}>{item.product_name}</TableCell>
              <TableCell
                sx={{
                  py: 0.75,
                  fontSize: 12,
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                }}
              >
                {item.sku}
              </TableCell>
              <TableCell align="center" sx={{ py: 0.75, fontSize: 12 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#eee',
                    borderRadius: '4px',
                    px: 1,
                    py: 0.25,
                    fontWeight: 600,
                    fontSize: 11,
                    minWidth: 24,
                  }}
                >
                  {item.quantity}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ py: 0.75, fontSize: 12, color: 'text.secondary' }}>
                {formatCurrency(item.unit_price)}
              </TableCell>
              <TableCell align="right" sx={{ py: 0.75, fontSize: 12, fontWeight: 600 }}>
                {formatCurrency(item.total)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>

    <Divider />

    {/* Payment breakdown */}
    <Box>
      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Payment
      </Typography>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
        <Field label="Payment Type" value={detail.payment_type} />
        <Field label="Payment Status" value={detail.payment_status} />
        {detail.order_tid && <Field label="Transaction ID" value={detail.order_tid} />}
        {detail.order_rid && <Field label="Reference ID" value={detail.order_rid} />}
        <Field label="Subtotal" value={formatCurrency(detail.order_subtotal)} />
        <Field label="Delivery Fee" value={formatCurrency(detail.order_deliverfee)} />
        {detail.order_discount > 0 && (
          <Field label="Discount" value={`-${formatCurrency(detail.order_discount)}`} />
        )}
        {detail.order_level_discount > 0 && (
          <Field label="Order Discount" value={`-${formatCurrency(detail.order_level_discount)}`} />
        )}
        {detail.credit_used != null && detail.credit_used > 0 && (
          <Field label="Credit Used" value={formatCurrency(detail.credit_used)} />
        )}
        <Field label="Total" value={<strong>{formatCurrency(detail.order_total)}</strong>} />
      </Box>
    </Box>
  </Box>
);

// ─── Status History Tab ──────────────────────────────────────────────────────

const StatusHistoryTab = ({ detail }: { detail: IOrderDetail }) => {
  if (!detail.status_history?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No status history recorded.
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {detail.status_history.map((h, idx) => (
        <Box key={idx} display="flex" gap={1.5} alignItems="flex-start">
          <Chip label={h.status} size="small" color={statusColor(h.status)} sx={{ minWidth: 90 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {formatDate(h.changed_at)} {h.changed_by ? `· ${h.changed_by}` : ''}
            </Typography>
            {h.metadata && (
              <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                {Object.entries(h.metadata).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${formatMetadataKey(key)}: ${value}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11, height: 20 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
